import React, { ReactNode } from 'react';
import {
  CompanyDocuments,
  DocumentReferenceObjectCreate,
  DocumentReferencesView,
  FileView,
  FolderView,
} from 'api';
import { RequestedFileExtended } from 'common/types/files';
import { Answer, QuestionType } from 'common/types/firm';
import { CurrentCompanyMeasurementDate } from 'common/types/measurementDate';
import { RequestedFile } from 'pages/RequestDocuments/components/FolderIcon/types';
import { RolePermissions } from '../utils/rolePermissions';

export const PROGRESS_TYPE_DOCUMENTS = 'documents';
export const PROGRESS_TYPE_QUESTIONS = 'questions';
export const TAB_ID_DOCUMENTS = 'documents';
export const TAB_ID_QUESTIONS = 'questions';
export const TAB_LABEL_DOCUMENTS = 'Documents';
export const TAB_LABEL_QUESTIONS = 'Questions';

type User = {
  id: number;
  full_name: string;
  email: string;
};

type DelegatedResponsibleParty = {
  name: string;
  email: string;
};

type DelegationThreadItem = {
  name: string;
  email: string;
  role: string;
};

type BaseProgress = {
  id: number;
  created_at: string;
  updated_at: string;
  name: string;
};

export type DocumentProgress = BaseProgress & {
  files: FileView[];
  is_uploaded: boolean;
  tags: null;
  fulfillment_status?: 'pending' | 'uploaded' | 'not_applicable' | 'not_available';
  fulfillment_note?: string | null;
  is_fulfilled?: boolean;
  delegated_responsible_party?: DelegatedResponsibleParty | null;
  delegation_thread?: DelegationThreadItem[];
};

export type QuestionProgress = BaseProgress & {
  children_questions: [];
  question_type?: QuestionType;
  options: [];
  answers: Array<Answer>;
  parent_question: null;
  created_at: Date;
  updated_at?: Date;
};

export interface CurrentRequestedDocumentTablesProps {
  documents: {
    id: number | undefined;
    responsible_users: Array<{ full_name: string }>;
    delegated_responsible_party?: DelegatedResponsibleParty | null;
    delegation_thread?: DelegationThreadItem[];
    delegation_depth?: number;
    progress: RequestedFileExtended[];
  };
  unmetRequests?: Array<DocumentProgress>;
  setUploadedFiles?: React.Dispatch<React.SetStateAction<RequestedFile[]>>;
  questions?: Array<QuestionProgress>;
  searchQuery?: string;
}

export interface CurrentRequestedQuestionsListProps {
  questions: {
    id: number;
    responsible_users: Array<{ full_name: string }>;
    progress: Array<QuestionProgress>;
  };
}

export type CurrentInformationRequest = {
  id: number;
  progress: (DocumentProgress | QuestionProgress)[];
  last_interaction: string;
  responsible_users: User[];
  delegated_responsible_party?: DelegatedResponsibleParty | null;
  delegation_thread?: DelegationThreadItem[];
  delegation_depth?: number;
  is_deleted: boolean;
  deleted_at: string;
  assigned_name: null;
  assigned_email: null;
  summary: string;
  description: null;
  is_done: boolean;
  extension_of: null;
  company_measurement_date: number;
  requesting_user: number;
  category: number;
};

export type CurrentRequestedDocumentsAndQuestionsProps = {
  currentInformationRequests: CurrentInformationRequest[];
};

export interface CurrentRequestedDocumentProps extends CurrentRequestedDocumentsAndQuestionsProps {}

interface SubTitle {
  id: number;
  label: string;
}

type FilteredQuestions = {
  responsible_users: User[];
  progress: QuestionProgress[];
};

type FilteredDocuments = {
  responsible_users: User[];
  progress: DocumentProgress[];
};

export type CurrentRequestedTasksAccordionProps = {
  id: number;
  title: string;
  subTitles: SubTitle[];
  childrenComponent: ReactNode;
  values: FilteredQuestions | FilteredDocuments;
};
export type FolderFileMap = { [key: number]: Array<DocumentReferencesView> };
export type FolderOpenMap = { [key: number]: { open: boolean; parent: number | undefined } };
export type FolderMap = { [key: number]: FolderView };
export type FileRequestMap = { [key: number]: Array<DocumentProgress> };

export type ExtendedFolderView = FolderView & {
  files?: Array<DocumentReferencesView>;
  childFolders?: Array<ExtendedFolderView>;
};

export type CurrentRequestedDocumentTableRowProps = {
  row: DocumentReferencesView;
  fileRequestMap: FileRequestMap;
  setSelectedFile: React.Dispatch<React.SetStateAction<DocumentReferencesView | undefined>>;
  setSendFile: React.Dispatch<React.SetStateAction<DocumentReferencesView | undefined>>;
  downloadDocument: (file: DocumentReferencesView) => void;
  setDraggedRow: (row: DocumentReferencesView | null) => void;
  setDraggedSelectedFiles?: (files: DocumentReferencesView[] | null) => void;
  showRow: boolean;
  selectedFiles: DocumentReferencesView[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<DocumentReferencesView[]>>;
  rowClass: string;
};

type AddFilesToFolder = (
  documentId: number,
  data: { folder_id: number | undefined; document_references_ids: number[] },
  showMsg: boolean
) => Promise<void>;

export interface DocumentCountDisplayProps {
  currentDocuments: CompanyDocuments;
}

export interface DocumentToolbarProps extends DocumentCountDisplayProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

export type CurrentRequestedDocumentTableFolderRowProps = {
  folder: FolderView;
  refreshDocuments: () => void;
  addFilesToFolder: AddFilesToFolder;
  draggedRow: DocumentReferencesView | null;
  draggedSelectedFiles?: DocumentReferencesView[];
  onDropSuccess?: (droppedFiles: DocumentReferencesView[]) => void;
  toggleFolder: (folderId: number, childFolderIds: number[]) => void;
  isFolderOpen: boolean;
  showFolder: boolean;
  setSelectedFolder: React.Dispatch<React.SetStateAction<FolderView | undefined>>;
  currentDocumentsId: CompanyDocuments['id'];
  folderStatusMap: FolderOpenMap;
};

export type DropProps = {
  draggedRow: DocumentReferencesView | null;
  draggedSelectedFiles?: DocumentReferencesView[];
  addFilesToFolder: AddFilesToFolder;
  refreshDocuments: () => void;
};

export type OnSaveFunction = (
  file: FileView,
  selectedFileRequestIds: number[],
  deselectedFileRequestIds: number[],
  referencesToAdd: DocumentReferenceObjectCreate[],
  referencesToRemove: number[]
) => Promise<void>;

export interface EditFileDialogProps {
  open: boolean;
  onClose: () => void;
  file: DocumentReferencesView;
  fileRequests: DocumentProgress[];
  fileRequestMap: FileRequestMap;
  onSave: OnSaveFunction;
}

export type OnSendFunction = (file: DocumentReferencesView, measurementDateId: number) => Promise<void>;

export interface SendFileDialogProps {
  open: boolean;
  onClose: () => void;
  file?: DocumentReferencesView;
  files?: DocumentReferencesView[];
  measurementDates: CurrentCompanyMeasurementDate[];
}

export interface EditFolderDialogProps {
  open: boolean;
  onClose: () => void;
  folder: FolderView;
  onSave: (name: string, parentFolder: FolderView | undefined) => void;
  folders?: FolderView[];
}

export interface ProgressProps {
  display: string;
  value: number;
  completed: number;
  total: number;
  pending: number;
  type?: typeof PROGRESS_TYPE_DOCUMENTS | typeof PROGRESS_TYPE_QUESTIONS;
}

export interface OptionInterface {
  objectId: number | null;
  isDisabled: boolean;
  reference_object_id: number | null;
  reference_object_type: string | null;
  name: string;
  slug: string;
  type: string;
}

export interface FormValuesInterface {
  references: OptionInterface[];
}

export interface CurrentRequestedDocumentTableMultipleSelectedBoxProps {
  selectedFiles: DocumentReferencesView[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<DocumentReferencesView[]>>;
}

export interface ActionMenuProps {
  anchorEl: HTMLElement | null;
  handleClose: () => void;
  handleDownloadAll: () => void;
  handleDeleteAll: () => void;
  handleMoveAll: () => void;
  permissions: RolePermissions;
}
